import { motion } from "framer-motion";
import { ArrowDown } from "lucide-react";

export default function Hero() {
  const scrollTo = (id) => () => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden bg-[#0A0A0A] flex items-center justify-center"
    >
      {/* Soft ambient glow */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(212,175,55,0.09)_0%,_transparent_65%)]" />

      <div className="relative z-10 max-w-5xl mx-auto px-6 lg:px-12 text-center flex flex-col items-center">
        {/* Small eyebrow */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="flex items-center gap-4"
        >
          <span className="block h-px w-8 bg-[#B8873B]/70" />
          <span className="text-[10px] sm:text-[11px] tracking-[0.55em] uppercase text-[#B8873B]">
            August 7th, 2026 · Harvest International School
          </span>
          <span className="block h-px w-8 bg-[#B8873B]/70" />
        </motion.div>

        {/* Wordmark */}
        <motion.h1
          initial={{ opacity: 0, y: 20, letterSpacing: "0.1em" }}
          animate={{ opacity: 1, y: 0, letterSpacing: "0.08em" }}
          transition={{ duration: 1.4, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
          data-testid="hero-headline"
          className="font-bodoni font-normal text-gold-gradient mt-10 sm:mt-12 leading-none text-[3.5rem] sm:text-8xl md:text-9xl lg:text-[12rem]"
          style={{ letterSpacing: "0.08em" }}
        >
          TRADELLA
        </motion.h1>

        {/* Hairline divider */}
        <motion.div
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 1, delay: 1, ease: [0.22, 1, 0.36, 1] }}
          className="h-px w-40 sm:w-56 bg-gradient-to-r from-transparent via-[#B8873B] to-transparent mt-10 sm:mt-12"
        />

        {/* Motto */}
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.15 }}
          data-testid="hero-motto"
          className="font-bodoni italic mt-8 sm:mt-10 text-marble text-xl sm:text-2xl md:text-3xl lg:text-4xl tracking-[0.06em]"
        >
          Forge Your Fortune
        </motion.p>

        {/* Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.4 }}
          className="mt-12 sm:mt-14 flex flex-col sm:flex-row gap-3 sm:gap-4"
        >
          <button
            onClick={scrollTo("register")}
            data-testid="hero-register-btn"
            className="btn-sheen px-8 py-4 bg-[#B8873B] text-black font-semibold uppercase tracking-[0.32em] text-[11px] sm:text-xs hover:bg-[#E6C88A] transition-colors duration-300"
          >
            Register Now
          </button>
          <button
            onClick={scrollTo("events")}
            data-testid="hero-explore-btn"
            className="px-8 py-4 border border-[#B8873B]/60 text-[#B8873B] uppercase tracking-[0.32em] text-[11px] sm:text-xs hover:border-[#B8873B] hover:bg-[#B8873B]/5 transition-colors duration-300"
          >
            Explore Events
          </button>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.button
        onClick={scrollTo("about")}
        data-testid="hero-scroll-indicator"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 1.8 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-[12] flex flex-col items-center gap-2 text-[#B8873B]/60 hover:text-[#B8873B] transition-colors"
      >
        <span className="text-[10px] tracking-[0.4em] uppercase">Scroll</span>
        <ArrowDown size={16} className="animate-bounce" />
      </motion.button>
    </section>
  );
}
